import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import _, { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import { useHistory } from 'react-router-dom'

import LoadingIcon from 'components/atoms/LoadingIcon'
import notify from 'components/atoms/Notify'
import Table from 'components/pageComponents/Table'
import PageFooter from 'components/paginationComponent'
import EmptyState from 'components/pageComponents/EmptyState'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import { IoSearch } from 'react-icons/io5'

const WalletPayoutRequest = () => {
  const { t } = useTranslation()
  const [payoutRequests, setPayoutRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [payoutRequestLoading, setPayoutRequestLoading] = useState(false)
  const [pagination, setPagination] = useState({
    pageSize: 20,
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
  })
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})

  const history = useHistory()

  useEffect(() => {
    fetchPayoutRequests(1, pagination.pageSize, search)
  }, [search, sort])

  const fetchPayoutRequests = async (page = 1, pageSize = 20, query = '') => {
    try {
      setLoading(true)

      const response = await apiGet('wallet-payout-requests', {
        pageNumber: page,
        pageSize,
        query: query || undefined,
        sortBy: sort.sortBy || undefined,
        sortOrder: sort.sortOrder || undefined,
      })

      if (response?.data) {
        setPayoutRequests(page === 1 ? response.data.payoutRequests || [] : [...payoutRequests, ...(response.data.payoutRequests || [])])
        setPagination(response.data.pagination || {})
      } else {
        setPayoutRequests(page === 1 ? [] : payoutRequests)
      }
    } catch (error) {
      notify.error(t('walletPayoutRequest.messages.failedToFetchPayoutRequests'))
      console.error('Fetch payout requests error:', error)
      setPayoutRequests(page === 1 ? [] : payoutRequests)
    } finally {
      setLoading(false)
    }
  }

  const handlePayoutRequestAction = async (payoutRequestId, action) => {
    setPayoutRequestLoading(`${payoutRequestId}-${action}`)
    try {
      const response = await apiPost(`verify/payout-request/${payoutRequestId}`, { action })
      if (response.success) {
        const successMessage =
          action === 'confirmed' ? t('walletPayoutRequest.messages.payoutRequestAcceptedSuccessfully') : t('walletPayoutRequest.messages.payoutRequestRejectedSuccessfully')
        notify.success(successMessage)
        fetchPayoutRequests(1, pagination.pageSize, search)
      }
    } catch (error) {
      const errorMessage = action === 'confirmed' ? t('walletPayoutRequest.messages.failedToAcceptPayoutRequest') : t('walletPayoutRequest.messages.failedToRejectPayoutRequest')
      notify.error(errorMessage)
      console.error('Payout request action error:', error)
    } finally {
      setPayoutRequestLoading(false)
    }
  }

  const getPayoutStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
      case 'confirmed':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = status => {
    switch (status) {
      case 'pending':
        return t('walletPayoutRequest.status.pending')
      case 'approved':
        return t('walletPayoutRequest.status.approved')
      case 'confirmed':
        return t('walletPayoutRequest.status.confirmed')
      case 'rejected':
        return t('walletPayoutRequest.status.rejected')
      default:
        return _.toUpper(status)
    }
  }

  const columns = [
    {
      name: t('walletPayoutRequest.columns.requestedBy'),
      width: '20%',
      key: 'requestedBy',
      sortable: true,
      rendered: item => (
        <div>
          <div className="text-sm font-semibold text-gray-900">{item.requestedBy?.fullName || ''}</div>
          <div className="text-xs text-gray-500">{item.requestedBy?.email || ''}</div>
        </div>
      ),
    },
    {
      name: t('walletPayoutRequest.columns.status'),
      width: '10%',
      key: 'status',
      sortable: true,
      rendered: item => <span className={`inline-flex px-3 py-1 text-[13px] rounded-full ${getPayoutStatusColor(item.status)}`}>{getStatusText(item.status)}</span>,
    },
    {
      name: t('walletPayoutRequest.columns.amount'),
      width: '10%',
      key: 'payoutBalance',
      sortable: true,
      rendered: item => <div className="text-[13px]">OMR {item.payoutBalance}</div>,
    },
    {
      width: '5%',
      name: t('walletPayoutRequest.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        return createdTime ? moment(createdTime).fromNow() : ''
      },
    },
    {
      width: '5%',
      name: t('walletPayoutRequest.columns.lastUpdated'),
      key: 'updatedAt',
      sortable: true,
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        return updatedTime ? moment(updatedTime).fromNow() : ''
      },
    },
    {
      name: t('walletPayoutRequest.columns.actions'),
      width: '20%',
      key: 'actions',
      align: 'left',
      rendered: item =>
        item.status === 'pending' && (
          <div className="flex space-x-2">
            <button
              onClick={() => handlePayoutRequestAction(item._id, 'confirmed')}
              disabled={payoutRequestLoading === `${item._id}-confirmed`}
              className="w-full flex justify-center py-1 px-1 border border-transparent rounded-md shadow-sm text-sm text-normal text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
            >
              {payoutRequestLoading === `${item._id}-confirmed` ? t('walletPayoutRequest.buttons.processing') : t('walletPayoutRequest.buttons.accept')}
            </button>
            <button
              onClick={() => handlePayoutRequestAction(item._id, 'rejected')}
              disabled={payoutRequestLoading === `${item._id}-rejected`}
              className="w-full flex justify-center py-1 px-1 border border-transparent rounded-md shadow-sm text-sm text-normal text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {payoutRequestLoading === `${item._id}-rejected` ? t('walletPayoutRequest.buttons.processing') : t('walletPayoutRequest.buttons.reject')}
            </button>
          </div>
        ),
    },
  ]

  return (
    <Layout
      title={t('walletPayoutRequest.title')}
      headerRight={
        <div className="flex items-center justify-end gap-4">
          <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />

          <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="mr-3 h-4 w-4 text-gray-400" />
            </div>
            <input
              onChange={e => setSearch(e.target.value)}
              value={search}
              type="text"
              name="search"
              id="search"
              className="block w-full pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
              placeholder={t('users.filters.searchPlaceholder')}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {payoutRequests?.length === 0 ? (
                      <EmptyState v2 description={t('walletPayoutRequest.emptyState.description')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchPayoutRequests(pagination?.currentPage + 1, pagination.pageSize, search)}
                        sortBy={sort.sortBy}
                        sortOrder={sort.sortOrder}
                        handleSort={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })}
                        data={payoutRequests}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default WalletPayoutRequest
